/**
 * Privacy Policy page. Canonical English source: docs/PRIVACY_POLICY.md
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Seo from '../components/Seo';
import './LegalPage.css';

const Privacy = () => {
  const { isEn, t, lang, localePath } = useLanguage();

  return (
    <div className="legal-page">
      <Seo
        title={t('legal.privacyTitle')}
        description={t('legal.privacyDescription')}
        path="/privacy"
        lang={lang}
        hreflang
      />
      {isEn ? (
        <div className="container">
          <article className="legal-page__inner">
            <p className="legal-page__eyebrow">Last updated: 6 September 2026</p>
            <h1>Privacy Policy</h1>
            <p>
              This policy explains how ClarCV (the “Service”) collects, uses, and protects your
              information when you use the platform. If you do not agree, do not use the Service.
            </p>

            <h2>1. Controller</h2>
            <p>
              The data controller is ClarCV (or the legal entity operating the Service). Contact:
              privacy@clarcv.com.
              For product support or feedback: hola@clarcv.com.
            </p>

            <h2>2. Data we collect</h2>
            <ul>
              <li>Account: email, password (hashed) when you register with email, name, and profile details.</li>
              <li>
                Google Sign-In: if you choose “Sign in with Google”, Google sends us your email,
                name, and a Google account identifier after you consent. We do not receive your
                Google password.
              </li>
              <li>CV and usage: uploaded files, analysis results, templates, and downloads.</li>
              <li>Technical: IP, browser, device, and access logs.</li>
            </ul>
            <p>
              We do not store card data on our servers when payment is handled by Stripe.
            </p>

            <h2>3. How we use data</h2>
            <ul>
              <li>Create and manage your account.</li>
              <li>Analyze and improve CVs, apply templates, and generate PDF/Word.</li>
              <li>Improve the Service, security, and legal compliance.</li>
            </ul>
            <p>We do not sell your personal data.</p>

            <h2>4. Artificial intelligence</h2>
            <p>
              To analyze and improve your CV we use AI providers (e.g. OpenAI) as processors. We send
              the textual CV content needed for the request. We do not send passwords or payment data. AI
              processing happens only with your explicit consent in the analysis flow.
            </p>
            <p>
              <strong>Your CV is sent only to fulfil your request</strong> (ATS analysis or rewrite).
              We do <strong>not</strong> authorize the provider to use your CV to train general-purpose
              models. We do not sell your data. ClarCV stores what is needed for your account (file,
              analysis, downloads) while the account is active. You can delete your account in
              Account, or request deletion at privacy@clarcv.com.
            </p>
            <p>
              Plain-language guide:{' '}
              <Link to={localePath('/blog/privacidad-ia-cv')}>What happens to your CV when ClarCV uses AI</Link>.
            </p>

            <h2>5. Retention and rights</h2>
            <p>
              We retain data while the account is active and as required by law. You may request
              access, rectification, erasure, restriction, objection, and portability, and withdraw
              AI-processing consent. Erasure is available in Account (“Delete my account”) or by
              contacting the address in section 1.
            </p>

            <h2>6. Cookies and analytics</h2>
            <p>
              We use cookies needed for session and preferences (for example login and language).
              These are necessary for the Service to work.
            </p>
            <p>
              After you accept analytics cookies via the cookie banner, we also use Google Analytics 4
              (Google Ireland Limited / Google LLC) to measure aggregated traffic and product usage on
              clarcv.com (for example pages visited and approximate location derived from IP). Google
              may set its own cookies or similar technologies. See{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                Google’s Privacy Policy
              </a>
              .
            </p>
            <p>
              Separately, you may opt in to <strong>session recording</strong> with Microsoft Clarity
              (Microsoft Corporation). That checkbox is <strong>off by default</strong> and is not
              required to use ClarCV. If you enable it, Clarity may record your session on marketing
              pages <strong>and</strong> on product screens (upload, editor, ATS view, account).
              Masking is set to <strong>Relaxed</strong>, so recordings may show{' '}
              <strong>readable</strong> CV text and most on-screen form content. Password fields are
              masked in our UI. Recordings are kept for a short period (typically 14–30 days). Clarity
              may set cookies or similar technologies; see{' '}
              <a
                href="https://clarity.microsoft.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Microsoft Clarity privacy
              </a>
              . Legal basis: your consent (GDPR Art. 6(1)(a)). You can withdraw anytime via Cookies in
              the footer; withdrawing reloads the page so recording scripts unload.
            </p>
            <p>
              If you accept analytics but leave session recording unchecked, we may still use Clarity
              on public marketing pages and the file-choice screen (
              <code>/dashboard/from-file</code>) for heatmaps — not on editor, upload, or CV preview.
            </p>
            <p>
              You can accept or reject analytics in the banner (Reject is as easy as Accept) and change
              that later from Cookies in the footer. Blocking analytics or recording does not block
              login or CV tools.
            </p>
            <p>
              If you use Google Sign-In, Google also processes your authentication data under its
              own policy. See{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                Google’s Privacy Policy
              </a>
              .
            </p>

            <p className="legal-page__note">
              Questions: privacy@clarcv.com. See also the <Link to={localePath('/terms')}>Terms of Use</Link>.
            </p>
          </article>
        </div>
      ) : (
        <div className="container">
          <article className="legal-page__inner">
          <p className="legal-page__eyebrow">Última actualización: 6 septiembre 2026</p>
          <h1>Política de privacidad</h1>
          <p>
            Esta política explica cómo ClarCV (“el Servicio”) recoge, usa y protege tu información
            cuando usas la plataforma. Si no estás de acuerdo, no uses el Servicio.
          </p>

          <h2>1. Responsable</h2>
          <p>
            El responsable del tratamiento es ClarCV (o la entidad legal que opere el Servicio).
            Contacto: privacy@clarcv.com.
            Para soporte de producto o feedback: hola@clarcv.com.
          </p>

          <h2>2. Datos que recogemos</h2>
          <ul>
            <li>Cuenta: correo, contraseña (hash) si te registras con email, nombre y datos de perfil.</li>
            <li>
              Acceso con Google: si eliges “Entrar con Google”, Google nos envía tu correo, nombre
              y un identificador de cuenta de Google tras tu consentimiento. No recibimos tu
              contraseña de Google.
            </li>
            <li>CV y uso: archivos subidos, resultados de análisis, plantillas y descargas.</li>
            <li>Técnicos: IP, navegador, dispositivo y registros de acceso.</li>
          </ul>
          <p>
            No almacenamos datos de tarjeta en nuestros servidores cuando el pago lo gestiona Stripe.
          </p>

          <h2>3. Uso de los datos</h2>
          <ul>
            <li>Crear y gestionar tu cuenta.</li>
            <li>Analizar y mejorar CVs, aplicar plantillas y generar PDF/Word.</li>
            <li>Mejorar el Servicio, seguridad y cumplimiento legal.</li>
          </ul>
          <p>No vendemos tus datos personales.</p>

          <h2>4. Inteligencia artificial</h2>
          <p>
            Para analizar y mejorar tu CV usamos proveedores de IA (p. ej. OpenAI) como encargados
            del tratamiento. Enviamos el contenido textual necesario del CV. No enviamos contraseñas
            ni datos de pago. El tratamiento con IA se realiza solo con tu consentimiento explícito
            en el flujo de análisis.
          </p>
          <p>
            <strong>Tu CV se envía solo para cumplir tu petición</strong> (análisis ATS o mejora).
            <strong> No</strong> autorizamos al proveedor a usar tu CV para entrenar modelos de
            propósito general. No vendemos tus datos. ClarCV guarda lo necesario para tu cuenta
            (archivo, análisis, descargas) mientras la cuenta esté activa. Puedes eliminar la cuenta
            en Cuenta, o pedir el borrado en privacy@clarcv.com.
          </p>
          <p>
            Guía en lenguaje claro:{' '}
            <Link to={localePath('/blog/privacidad-ia-cv')}>Qué pasa con tu CV cuando usas IA en ClarCV</Link>.
          </p>

          <h2>5. Conservación y derechos</h2>
          <p>
            Conservamos los datos mientras la cuenta esté activa y el tiempo necesario por obligaciones
            legales. Puedes solicitar acceso, rectificación, borrado, limitación, oposición y
            portabilidad, y retirar el consentimiento del tratamiento con IA. El borrado está en
            Cuenta (“Eliminar mi cuenta”) o en la dirección de la sección 1.
          </p>

          <h2>6. Cookies y analítica</h2>
          <p>
            Usamos cookies necesarias para sesión y preferencias (por ejemplo inicio de sesión e
            idioma). Son imprescindibles para que el Servicio funcione.
          </p>
          <p>
            Si aceptas las cookies de analítica en el banner, también usamos Google Analytics 4
            (Google Ireland Limited / Google LLC) para medir de forma agregada el tráfico y el uso
            del producto en clarcv.com (por ejemplo páginas visitadas y ubicación aproximada a partir
            de la IP). Google puede establecer cookies u otras tecnologías similares. Consulta la{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
              Política de privacidad de Google
            </a>
            .
          </p>
          <p>
            Por separado puedes activar la <strong>grabación de sesión</strong> con Microsoft Clarity
            (Microsoft Corporation). Esa casilla está <strong>desmarcada por defecto</strong> y no es
            obligatoria para usar ClarCV. Si la activas, Clarity puede grabar tu sesión en páginas de
            marketing <strong>y</strong> en pantallas del producto (subida, editor, vista ATS, cuenta).
            El enmascarado está en <strong>Relaxed</strong>, así que las grabaciones pueden mostrar el
            texto de tu CV y la mayoría del contenido de formularios de forma <strong>legible</strong>.
            Los campos de contraseña se enmascaran en nuestra interfaz; las grabaciones se conservan
            poco tiempo (habitualmente 14–30 días). Clarity puede establecer cookies u otras
            tecnologías similares; consulta la{' '}
            <a
              href="https://clarity.microsoft.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              privacidad de Microsoft Clarity
            </a>
            . Base jurídica: tu consentimiento (RGPD art. 6.1.a). Puedes retirarlo en cualquier
            momento desde Cookies en el pie; al retirarlo se recarga la página para descargar los
            scripts de grabación.
          </p>
          <p>
            Si aceptas analítica pero dejas la grabación desmarcada, podemos usar Clarity solo en
            páginas públicas de marketing y en la pantalla de elección (
            <code>/dashboard/from-file</code>) para mapas de calor — no en el editor, la subida ni la
            vista previa del CV.
          </p>
          <p>
            Puedes aceptar o rechazar la analítica en el banner (Rechazar es tan fácil como Aceptar) y
            cambiarlo después desde Cookies en el pie. Bloquear la analítica o la grabación no impide
            iniciar sesión ni usar las herramientas de CV.
          </p>
          <p>
            Si usas el acceso con Google, Google también trata tus datos de autenticación según su
            propia política. Consulta la{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
              Política de privacidad de Google
            </a>
            .
          </p>

          <p className="legal-page__note">
            Consultas: privacy@clarcv.com. Ver también los{' '}
            <Link to={localePath('/terms')}>Términos de uso</Link>.
          </p>
        </article>
        </div>
      )}
    </div>
  );
};

export default Privacy;
